<template>
  <div>
    <Breadcrumb
      :items="breadcrumb"
      with-brodos
    />

    <main data-page-type="shop">
      <Heading
        class="shop-list__headline text--center"
        spaces-bottom-rem
        spaces-top-rem
      >
        <span v-if="searchTerm">
          Deine Suche nach <strong>{{ truncatedSearchTerm }}</strong>
        </span>
        <span v-else>{{ pageHeadline }}</span>
      </Heading>

      <div
        v-if="displayFiltersAndCount"
        class="shop-list__results-count text--center"
        data-nosnippet
      >
        {{ offersCount }} <template v-if="offersCount === 1">Produkt</template
        ><template v-else>Produkte</template>
      </div>

      <FilterAccordion
        v-if="displayFiltersAndCount"
        :aggregations="aggregations"
        :current-offers-count="offersCount"
        :item-urls="itemUrls"
        :variables="listViewParams"
        :hidden-filters="hiddenFilters"
        @submit="refreshOffers"
      />

      <GridContainer>
        <OfferTilesContainer
          v-if="hasOffers && offers?.entries"
          :offers="offers.entries"
          class="shop-list__offer-tiles"
        />
        <div
          v-else
          class="shop-list__empty-result text--center"
        >
          <template v-if="status === 'pending'">Bitte warten...</template>
          <template v-else>Es wurden leider keine Produkte gefunden.</template>
        </div>

        <div
          v-if="showPagination"
          class="shop-list__pagination-container"
        >
          <Pagination
            class="shop-list__pagination"
            :current-page="pagination?.page ?? 1"
            :total-pages="pagination?.totalPages ?? 1"
            :url="pageUrl"
          />
          <PageLengthSelector
            class="shop-list__page-length"
            :current-page-length="pagination?.size ?? 0"
            @change="pageLengthChanged"
          />
        </div>
      </GridContainer>

      <ShopListSeoContent
        :page-headline="pageHeadline"
        :route-slug="$route?.path || ''"
      />
    </main>
  </div>
</template>

<script lang="ts" setup>
import { setResponseHeader } from 'h3';
import {
  useAsyncData,
  useNuxtApp,
  useRoute,
  useRouter,
  onMounted,
  navigateTo,
  useHead,
  useDowntimesStore,
} from '#imports';
import { truncate } from 'lodash-es';

import { checkCategoryFilters, getRedirectPath } from '~/lib/handle-list-filter-responses';
import calculateLastModified from '#shared/last-modified-offer';
import fetchOffers from '~/lib/goliath/offers';
import mergeFilterVariables from '#shared/filter-params/merge-variables';

import { GridContainer, Heading } from '@i22team/td-longtail-vue-components';

import Breadcrumb, { type BreadcrumbItem } from '~/components/breadcrumb.vue';
import FilterAccordion from '~/components/filter/filter-accordion.vue';
import OfferTilesContainer from '~/components/offer-tiles-container.vue';
import PageLengthSelector from '~/components/page-length-selector.vue';
import Pagination from '~/components/pagination.vue';
import ShopListSeoContent from '~/components/shop-list-seo-content.vue';
import { TrackAsync } from '~/helpers/page-tracking';
import { getHeader } from '~/helpers/page-header-compositon';
import { getMappedCategories, getSeoTemplateContent } from '~/lib/seo/seo-template';
import { isListPageIndexed } from '#shared/page-index-helper';
import { useNavigationStore } from '~/stores/navigation';
import { parseFilterParams, serializeFilterParams } from '#shared/filter-params';
import type { SafeLink } from '@unhead/vue';
import type { RouteLocationNormalized } from 'vue-router';
import { storeToRefs } from 'pinia';

const nuxtApp = useNuxtApp();
const $router = useRouter();
const $route = useRoute();
const config = useRuntimeConfig();

const event = useRequestEvent();
const { maintenanceDowntime } = storeToRefs(useDowntimesStore());

const getListViewParams = (route: RouteLocationNormalized) => {
  const parsedRouteParams = parseFilterParams(route) as FilterQuery;
  if (!parsedRouteParams) return undefined;
  parsedRouteParams.grouped = true;
  if (!parsedRouteParams.filter) {
    parsedRouteParams.filter = {};
  }
  parsedRouteParams.filter.listed = true;
  // All products should be displayed, so we disable the available filter.
  // parsedRouteParams.filter.productAvailable = true; // show only products with an offer (available)
  // parsedRouteParams.filter.hasAlwaysDisplayed = true; // AND show products without an offer, when they have the flag "always_display"

  if (route.query?.articleNumbers && typeof route.query.articleNumbers === 'string') {
    parsedRouteParams.filter.articleNumbers = route.query.articleNumbers.split(',');
  }

  return parsedRouteParams;
};

definePageMeta({
  name: 'ListPage',
  validate: (route: RouteLocationNormalized) => {
    const parsedRoute = getListViewParams(route);
    if (!parsedRoute) {
      return {
        status: 404,
        // Nuxt overwrites `data` in validate results, so `statusText` is the marker for error.vue.
        statusText: 'Invalid list parameters',
      };
    }
    // store parsed data to avoid parsing them again
    route.meta.parsedRoute = parsedRoute;
    return true;
  },
});

watch(
  maintenanceDowntime,
  () => {
    if (maintenanceDowntime.value) {
      if (event) {
        // Make this page be stored not so long in nginx.
        setResponseHeader(event, 'Cache-Control', 'public, max-age=30');
      }
      if (!nuxtApp.$maintenanceRedirectUrl) {
        // store current url in context - works only csr.
        nuxtApp.provide('maintenanceRedirectUrl', $route.fullPath);
      }

      return navigateTo('/maintenance');
    }
  },
  { immediate: true }
);

const listViewParams = ref<FilterQuery>(
  // use stored params
  $route.meta?.parsedRoute || getListViewParams($route) || {}
);

const paginationSize = computed(() => listViewParams.value.pagination?.size);

watch(
  () => $route.fullPath,
  () => {
    listViewParams.value = getListViewParams($route) || {};
  }
);

const pageId = computed(() => JSON.stringify(listViewParams.value));

const {
  data: offers,
  error: errorData,
  status,
} = await useAsyncData(
  pageId,
  (_nuxtApp, { signal }) => fetchOffers(listViewParams.value, true, false, signal),
  {
    watch: [listViewParams],
  }
);

const redirectOn404 = () => {
  // We only want a redirect, when we have a valid URL.
  if (listViewParams.value !== undefined && Object.keys(listViewParams.value).length > 0) {
    const navigationStore = useNavigationStore();
    // Redirect to upper category if no offers found
    const redirectUrl = getRedirectPath(
      listViewParams.value.filter ?? undefined,
      navigationStore.navigationItems || []
    );
    if ($route.path !== redirectUrl) {
      return navigateTo(redirectUrl);
    }
  }

  // if there redirected page is the same or no page was found, redirect to the start page
  return navigateTo('/');
};

const itemUrl = (newVariables = {}) => {
  const variables = mergeFilterVariables(listViewParams.value, newVariables);
  const options = serializeFilterParams(variables);
  return $router.resolve(options).href;
};

const pageUrl = (currentPage: number | null) => {
  let page = currentPage;
  if (!page || page <= 1) page = null;

  return itemUrl({
    pagination: {
      page,
      size: paginationSize.value,
    },
  });
};

watch(
  errorData,
  () => {
    if (errorData.value) {
      if (errorData.value.statusCode === 404) {
        return redirectOn404();
      }
      // If the page is out of scope, redirect to the 1 page.
      if (
        errorData.value.statusCode === 422 &&
        errorData.value.message.includes('Maximum result window') &&
        listViewParams.value.pagination?.page !== 1
      ) {
        return navigateTo(pageUrl(1));
      }
      return showError(errorData.value);
    }
  },
  { immediate: true }
);

watch(
  offers,
  () => {
    if (offers.value) {
      // check unused categories from url and remove them to prevent a deadlock.
      const updatedUrlObject = checkCategoryFilters(listViewParams.value, offers.value);
      if (updatedUrlObject) {
        const updatedUrl = $router.resolve(updatedUrlObject)?.fullPath;
        if ($route.fullPath !== updatedUrl) {
          return navigateTo(updatedUrl);
        }
      }

      if (import.meta.server) {
        // redirect on server only (in FE we could have a deadlock filter)
        if (
          // check is response is empty
          (offers.value.pagination?.totalCount || 0) === 0 ||
          (offers.value.entries?.length || 0) === 0
        ) {
          return redirectOn404();
        }

        if (event && (offers.value.entries?.length || 0) > 0 && !event.node?.res?.headersSent) {
          setResponseHeader(
            event,
            'last-modified',
            calculateLastModified(offers.value, new Date(config.public.buildDate))
          );
        }
      }
    }
  },
  { immediate: true }
);

const seoTemplate = computed(() => getSeoTemplateContent(listViewParams.value, offers.value));

getHeader(
  {
    title: computed(() => seoTemplate.value.title),
    description: computed(() => seoTemplate.value.description),
    isIndexed: computed(() => isListPageIndexed(listViewParams.value, offers.value)),
  },
  {
    showCanonical: true,
  }
);

useHead(() => {
  const pagination = offers.value?.pagination;

  if (!pagination?.page || !pagination?.totalPages) return {};
  const safeLink: Partial<SafeLink>[] = [];
  const prevPage = pagination.page - 1;
  const prevPagePath = prevPage < 1 ? undefined : pageUrl(prevPage);
  if (prevPagePath) {
    safeLink.push({
      rel: 'prev',
      href: new URL(prevPagePath, config.public?.baseUrl).href,
    });
  }
  const nextPage = pagination.page + 1;

  const nextPagePath = nextPage > pagination.totalPages ? undefined : pageUrl(nextPage);
  if (nextPagePath) {
    safeLink.push({
      rel: 'next',
      href: new URL(nextPagePath, config.public?.baseUrl).href,
    });
  }
  return { link: safeLink };
});

onMounted(() => {
  TrackAsync(
    {
      page_type: 'product.catalog',
      // items: [offer.value],
    },
    { delay: 100 }
  );
});

const aggregations = computed(() => offers.value?.aggregations || {});

const searchTerm = computed(() => listViewParams.value.search);
const truncatedSearchTerm = computed(() => {
  if (!searchTerm.value) return undefined;
  const defuzzedSearchTerm = searchTerm.value?.replace(/\* /g, ' ').replace(/\*$/, '');
  return truncate(defuzzedSearchTerm, { length: 30, omission: '…' });
});

const pageHeadline = computed(() => seoTemplate.value.pageHeadline);

const breadcrumb = computed(() => {
  const items: BreadcrumbItem[] = [];

  const { selectedBrands, selectedProductCategories, ancestorProductCategories } =
    getMappedCategories(listViewParams.value, offers.value);

  ancestorProductCategories.forEach((productCategory) => {
    const to = serializeFilterParams({
      filter: { productCategoryPaths: [productCategory?.path] },
    } as FilterQuery);
    if (productCategory?.name && to) {
      items.push({
        label: productCategory?.name,
        to,
      });
    }
  });

  const productCategoryPaths = selectedProductCategories
    .map((category) => category.path)
    .filter(Boolean);

  if (selectedProductCategories.length) {
    const selectedProductCategoriesNames = selectedProductCategories.map(
      (category) => category.name
    );

    let label;
    if (selectedProductCategories.length > 2) {
      label = `${selectedProductCategoriesNames[0]}); ${selectedProductCategoriesNames[1]}, …`;
    } else {
      label = selectedProductCategoriesNames.join(', ');
    }

    items.push({
      label,
      to: serializeFilterParams({ filter: { productCategoryPaths } } as FilterQuery),
    });
  }

  if (selectedBrands.length) {
    const selectedBrandNames = selectedBrands.map((brand) => brand?.name).filter(Boolean);
    let label;
    if (selectedBrands.length > 2) {
      label = `${selectedBrandNames[0]}, ${selectedBrandNames[1]}, …`;
    } else {
      label = selectedBrandNames.join(', ');
    }
    const slugs = selectedBrands.map((brand) => brand?.slug).filter(Boolean);
    items.push({
      label,
      to: serializeFilterParams({
        filter: { productCategoryPaths, brandSlugs: slugs },
      } as FilterQuery),
    });
  }

  if (searchTerm.value) {
    items.push({ label: `Suche nach: ${truncatedSearchTerm.value}` });
  }

  return items;
});

const pagination = computed(() => offers.value?.pagination);

const offersCount = computed(() => offers.value?.variantsCount || 0);

const hasOffers = computed(() => offersCount.value > 0);

const showPagination = computed(() => (pagination.value?.totalPages ?? 0) > 1);

const hasFilters = computed(() => {
  if (!listViewParams.value?.filter) return false;
  const defaultFilterKeys = new Set(['listed', 'productAvailable', 'hasAlwaysDisplayed']);
  const hasNonDefaultFilter = Object.keys(listViewParams.value?.filter).filter(
    (key) => !defaultFilterKeys.has(key)
  );
  return hasNonDefaultFilter.length > 0;
});

const displayFiltersAndCount = computed(() => {
  if (hasFilters.value) return true;
  return hasOffers.value;
});

const productCategoryFilterUrl = (path: string) =>
  itemUrl({
    filter: { productCategoryPaths: [path] },
    pagination: { page: null, size: null },
  });
const brandFilterUrl = (slug: string) =>
  itemUrl({
    filter: { brandSlugs: [slug] },
    pagination: { page: null, size: null },
  });
const colorFilterUrl = (slug: string) =>
  itemUrl({
    filter: { colorSlugs: [slug] },
    pagination: { page: null, size: null },
  });
const compatibleProductFamilyFilterUrl = (slug: string) =>
  itemUrl({
    filter: { compatibleProductFamilySlugs: [slug] },
    pagination: { page: null, size: null },
  });
const productTagSlugsFilterUrl = (slug: string) =>
  itemUrl({
    filter: { productTagSlugs: [slug] },
    pagination: { page: null, size: null },
  });

const itemUrls = computed(() => ({
  productCategories: productCategoryFilterUrl,
  brands: brandFilterUrl,
  colors: colorFilterUrl,
  compatibleProductFamilies: compatibleProductFamilyFilterUrl,
  productTagSlugs: productTagSlugsFilterUrl,
}));

const mainCategory = computed(() => $route.path.split('/')[1]);

const hiddenFilters = computed(() => {
  const paths = listViewParams.value.filter?.productCategoryPaths || [];
  if (
    paths.includes('smartphones-und-handys') ||
    paths.includes('smartphones-und-handys/smartphones') ||
    paths.includes('tablets-und-zubehoer/tablets') ||
    paths.includes('wearables/smartwatches') ||
    paths.includes('wearables/fitnesstracker') ||
    paths.includes('tablets-und-zubehoer')
  ) {
    return ['compatibleProductFamilies'];
  }
  return [];
});

// This is needed as async data does not refetch data on query-only change
/* created() {
    if (process.client) {
      // When we have "%2B" (urlencoded "+") in the URL (from the server), the asyncData isn't called when the customer changes some filter. So we replace the URL.
      const replaced = this.$route.query?.wasReplaced;
      const replacedPath = this.$route.path.replace('%2B', '+');
      const navigate = (routeOptions) => $router.replace(routeOptions).catch(() => {});
      if (replacedPath !== this.$route.path && !replaced) {
        navigate({
          query: { ...this.$route.query, wasReplaced: true },
          path: replacedPath,
        });
      }
      // remove "replaced" query from URL when no encoded sign is present.
      else if (!this.$route.path.includes('%2B') && replaced) {
        const { wasReplaced, ...remainingQuery } = this.$route.query;
        navigate({
          query: remainingQuery,
          path: this.$route.path,
        });
      }
    }
  }, */

const refreshOffers = async (newVariables: FilterQuery) => {
  const variablesWithPageSize = mergeFilterVariables(
    {
      pagination: {
        size: paginationSize.value,
        page: 1,
      },
    },
    newVariables
  );

  await navigateTo(serializeFilterParams(variablesWithPageSize));
};

const pageLengthChanged = async (size: number) => {
  const variables = mergeFilterVariables<FilterQuery>(listViewParams.value, {
    pagination: { size, page: 1 },
  });

  await navigateTo(serializeFilterParams(variables));
};

// eslint-disable-next-line vue/no-expose-after-await
defineExpose({ mainCategory, listViewParams, aggregations, pageUrl, itemUrls, offers, breadcrumb });
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.shop-list {
  &__headline {
    position: relative;
    margin: {
      top: $grid-medium-gutter;
      bottom: $grid-small-gutter;
    }
  }

  &__results-count {
    font-size: 1.75rem;
    margin-bottom: $grid-medium-gutter;
    position: relative;
  }

  &__empty-result {
    margin-block: $grid-medium-gutter;
  }

  &__results {
    display: flex;
    overflow-x: hidden;
  }

  &__pagination-container {
    padding-inline: $grid-base;
    grid-gap: $grid-base * 2;
    margin-block: $grid-base * 2.5;
    display: flex;
    flex-direction: column;

    @include mq($mq-md) {
      display: grid;
      grid-template-columns: 195px auto 195px;
      margin-bottom: $grid-base * 4;
    }
  }

  &__pagination {
    grid-column: 2;
  }

  &__page-length {
    text-align: center;
    align-self: end;
    grid-column: 3;
  }

  &__promo-tile-badge {
    position: absolute;
    top: 40%;
    right: 2%;
    transform: scale(0.7);
    @include mq(480px) {
      transition: all ease 500ms;
      transform: scale(1);
    }
    @include mq($mq-sm) {
      top: 5%;
      left: 5%;
    }
    @include mq(800px) {
      left: 15%;
    }
    @include mq($mq-image-large) {
      top: 5%;
      left: 25%;
    }
    @include mq($mq-xl) {
      top: 8%;
      left: 30%;
    }
  }

  &__offer-tiles {
    position: relative;
    margin-bottom: $grid-base * 2;
  }

  &__seo {
    margin-block: $grid-base * 2;

    @include mq($mq-sm) {
      margin-block: $grid-base * 4;
    }
  }
}
</style>
